/**
 * 거래처 등록·수정 모달.
 *
 *   initial:
 *     undefined  — 닫힘
 *     null       — 새 거래처
 *     { id, ... } — 수정 모드
 *
 * 유형(GENERAL/PUBLIC) 선택에 따라 사업자등록번호 필수 여부가 달라진다.
 * 사업자등록증·통장사본 이미지 업로드는 entity 가 한 번 저장된 후(=id 확보 후) 가능하므로,
 * 새 거래처는 1) 기본 정보 저장 → 2) id 받은 뒤 이미지 업로드 가능 흐름.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirm as confirmDialog } from "../../../../util/confirmDialog";
import ActionButton from "../../../../component/common/ActionButton";
import {
    Box, Button, Divider, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import UploadFileRoundedIcon  from "@mui/icons-material/UploadFileRounded";
import OpenInNewRoundedIcon   from "@mui/icons-material/OpenInNewRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";

import { financeApi } from "../../api/financeApi";
import { notify }     from "../../../../util/toast";
import { formatPhone } from "../../../../util/phone";
import FormModal from "../../../../component/common/FormModal";

const EMPTY = {
    type: "GENERAL",
    name: "",
    bizRegistrationNo: "",
    representative: "",
    bizType: "",
    bizItem: "",
    address: "",
    phone: "",
    email: "",
    contactPerson: "",
    contactPhone: "",
    bankName: "",
    bankAccountNo: "",
    bankAccountHolder: "",
    memo: "",
};

export default function VendorEditDialog({ open, initial, onClose, onSaved }) {
    const isEdit = Boolean(initial?.id);
    const [form, setForm]       = useState(EMPTY);
    const [saving, setSaving]   = useState(false);
    const [licenseUrl, setLicenseUrl]   = useState(null); // 표시용 URL
    const [bankbookUrl, setBankbookUrl] = useState(null);
    const [vendorId, setVendorId]       = useState(null); // 새 등록 후 id 보관 (이미지 업로드용)
    const [attachments, setAttachments] = useState([]);

    const licenseInputRef    = useRef(null);
    const bankbookInputRef   = useRef(null);
    const attachmentInputRef = useRef(null);

    useEffect(() => {
        if (open === false) return;
        if (initial) {
            setForm({
                type: initial.type || "GENERAL",
                name: initial.name || "",
                bizRegistrationNo: initial.bizRegistrationNo || "",
                representative: initial.representative || "",
                bizType: initial.bizType || "",
                bizItem: initial.bizItem || "",
                address: initial.address || "",
                // 기존 비포맷 데이터도 입력 시점에 자동 하이픈 적용
                phone: formatPhone(initial.phone || ""),
                email: initial.email || "",
                contactPerson: initial.contactPerson || "",
                contactPhone: formatPhone(initial.contactPhone || ""),
                bankName: initial.bankName || "",
                bankAccountNo: initial.bankAccountNo || "",
                bankAccountHolder: initial.bankAccountHolder || "",
                memo: initial.memo || "",
            });
            setLicenseUrl(initial.bizLicenseImageUrl || null);
            setBankbookUrl(initial.bankbookImageUrl || null);
            setVendorId(initial.id || null);
            // 자유 첨부 목록도 같이 로드
            if (initial.id) {
                financeApi.listVendorAttachments(initial.id)
                    .then(res => setAttachments(res?.data ?? res ?? []))
                    .catch(() => setAttachments([]));
            } else {
                setAttachments([]);
            }
        } else {
            setForm(EMPTY);
            setLicenseUrl(null);
            setBankbookUrl(null);
            setVendorId(null);
            setAttachments([]);
        }
    }, [open, initial]);

    const reloadAttachments = async (id) => {
        try {
            const res = await financeApi.listVendorAttachments(id);
            setAttachments(res?.data ?? res ?? []);
        } catch { /* noop */ }
    };

    const handleAddAttachment = async (file) => {
        const id = vendorId || initial?.id;
        if (!id) {
            notify("먼저 거래처 정보를 저장한 뒤 첨부를 올려주세요.", "warning");
            return;
        }
        try {
            await financeApi.addVendorAttachment(id, file);
            notify("첨부가 추가되었습니다", "success");
            reloadAttachments(id);
        } catch (e) {
            notify(e?.message || "업로드 실패", "error");
        }
    };

    const handleDeleteAttachment = async (att) => {
        if (!(await confirmDialog({ message: `"${att.filename}" 첨부를 삭제하시겠습니까?` }))) return;
        try {
            await financeApi.deleteVendorAttachment(att.id);
            setAttachments(prev => prev.filter(a => a.id !== att.id));
        } catch (e) {
            notify(e?.message || "삭제 실패", "error");
        }
    };

    const isPublic  = form.type === "PUBLIC";
    const canSubmit = useMemo(() =>
        !!form.name?.trim() &&
        (isPublic || !!form.bizRegistrationNo?.trim()),
        [form, isPublic]);

    const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const handleSave = async () => {
        if (!canSubmit) {
            notify(isPublic ? "기관명을 입력하세요." : "사업자명·사업자등록번호는 필수입니다.", "warning");
            return;
        }
        setSaving(true);
        try {
            let saved;
            if (isEdit) {
                saved = await financeApi.updateVendor(initial.id, form);
            } else {
                saved = await financeApi.createVendor(form);
            }
            const result = saved?.data ?? saved;
            setVendorId(result?.id || null);
            notify(isEdit ? "수정되었습니다" : "등록되었습니다", "success");
            onSaved?.(result);
        } catch (e) {
            notify(e?.message || "저장 실패", "error");
        } finally { setSaving(false); }
    };

    const handleUpload = async (kind, file) => {
        const id = vendorId || initial?.id;
        if (!id) {
            notify("먼저 거래처 정보를 저장한 뒤 이미지를 올려주세요.", "warning");
            return;
        }
        try {
            const res = await financeApi.uploadVendorImage(id, kind, file);
            const dto = res?.data ?? res;
            if (kind === "license") setLicenseUrl(dto?.bizLicenseImageUrl || null);
            else                    setBankbookUrl(dto?.bankbookImageUrl || null);
            notify("이미지가 업로드되었습니다", "success");
        } catch (e) {
            notify(e?.message || "업로드 실패", "error");
        }
    };

    return (
        <FormModal
            open={!!open}
            onClose={onClose}
            title={isEdit ? "거래처 수정" : "새 거래처 등록"}
            maxWidth="md"
            onSave={handleSave}
            saving={saving}
            saveLabel={isEdit ? "수정 저장" : "등록"}
            saveDisabled={!canSubmit}
        >
                {/* 유형 */}
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569", mb: 0.6 }}>
                        유형
                    </Typography>
                    <ToggleButtonGroup
                        size="small" exclusive
                        value={form.type}
                        onChange={(_, v) => v && update("type", v)}>
                        <ToggleButton value="GENERAL" sx={{ textTransform: "none", fontWeight: 700, fontSize: 12 }}>일반 사업자</ToggleButton>
                        <ToggleButton value="PUBLIC"  sx={{ textTransform: "none", fontWeight: 700, fontSize: 12 }}>공공기관 / 국가기관</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Section title="기본 정보">
                    <Grid2>
                        <TextField label={isPublic ? "기관명 *" : "사업자명 *"} size="small" fullWidth
                            value={form.name} onChange={(e) => update("name", e.target.value)} />
                        <TextField label={isPublic ? "사업자등록번호 (선택)" : "사업자등록번호 *"} size="small" fullWidth
                            value={form.bizRegistrationNo} onChange={(e) => update("bizRegistrationNo", e.target.value)} />
                        <TextField label="대표자명" size="small" fullWidth
                            value={form.representative} onChange={(e) => update("representative", e.target.value)} />
                        <TextField label={isPublic ? "분류 (예: 중앙행정기관)" : "업태"} size="small" fullWidth
                            value={form.bizType} onChange={(e) => update("bizType", e.target.value)} />
                        <TextField label={isPublic ? "기관 코드 / 부서" : "종목"} size="small" fullWidth
                            value={form.bizItem} onChange={(e) => update("bizItem", e.target.value)} />
                        <TextField label="주소" size="small" fullWidth
                            value={form.address} onChange={(e) => update("address", e.target.value)} />
                    </Grid2>
                </Section>

                <Section title="연락처">
                    <Grid2>
                        <TextField label="전화번호" size="small" fullWidth
                            placeholder="010-0000-0000"
                            value={form.phone}
                            onChange={(e) => update("phone", formatPhone(e.target.value))} />
                        <TextField label="이메일" size="small" fullWidth
                            value={form.email} onChange={(e) => update("email", e.target.value)} />
                        <TextField label="담당자" size="small" fullWidth
                            value={form.contactPerson} onChange={(e) => update("contactPerson", e.target.value)} />
                        <TextField label="담당자 연락처" size="small" fullWidth
                            placeholder="010-0000-0000"
                            value={form.contactPhone}
                            onChange={(e) => update("contactPhone", formatPhone(e.target.value))} />
                    </Grid2>
                </Section>

                <Section title="입금 계좌">
                    <Grid2>
                        <TextField label="은행" size="small" fullWidth
                            value={form.bankName} onChange={(e) => update("bankName", e.target.value)} />
                        <TextField label="계좌번호" size="small" fullWidth
                            value={form.bankAccountNo} onChange={(e) => update("bankAccountNo", e.target.value)} />
                        <TextField label="예금주" size="small" fullWidth
                            value={form.bankAccountHolder} onChange={(e) => update("bankAccountHolder", e.target.value)} />
                    </Grid2>
                </Section>

                <Section title="첨부 이미지">
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <ImageUploadCard
                            title={isPublic ? "기관 증명서" : "사업자등록증"}
                            url={licenseUrl}
                            inputRef={licenseInputRef}
                            disabled={!vendorId && !initial?.id}
                            disabledHint="저장 후 업로드 가능"
                            onPick={(file) => handleUpload("license", file)} />
                        <ImageUploadCard
                            title="통장 사본"
                            url={bankbookUrl}
                            inputRef={bankbookInputRef}
                            disabled={!vendorId && !initial?.id}
                            disabledHint="저장 후 업로드 가능"
                            onPick={(file) => handleUpload("bankbook", file)} />
                    </Stack>
                </Section>

                <Section title="추가 첨부 (계약서·인감 등)">
                    <Stack spacing={1}>
                        {attachments.length === 0 ? (
                            <Box sx={{
                                p: 2, textAlign: "center",
                                border: "1px dashed #e2e8f0", borderRadius: 1.2,
                                fontSize: 12, color: "#94a3b8",
                            }}>
                                {(vendorId || initial?.id)
                                    ? "추가 첨부가 없습니다"
                                    : "거래처를 먼저 저장하면 첨부를 추가할 수 있습니다"}
                            </Box>
                        ) : attachments.map(a => (
                            <Stack key={a.id} direction="row" alignItems="center" spacing={1}
                                sx={{
                                    p: 1, borderRadius: 1, border: "1px solid #eef2f6",
                                    bgcolor: "#fafbfc",
                                }}>
                                {a.image && a.url ? (
                                    <Box component="img" src={a.url} alt=""
                                        sx={{ width: 40, height: 40, borderRadius: 0.8, objectFit: "cover", flexShrink: 0 }} />
                                ) : (
                                    <Box sx={{
                                        width: 40, height: 40, borderRadius: 0.8,
                                        bgcolor: "#eef2f6", color: "#64748b",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        flexShrink: 0,
                                    }}>
                                        <InsertDriveFileRoundedIcon sx={{ fontSize: 22 }} />
                                    </Box>
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{
                                        fontSize: 12.5, fontWeight: 700, color: "#0f172a",
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>
                                        {a.filename}
                                    </Box>
                                    <Box sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                                        {(a.sizeBytes != null ? Math.round(a.sizeBytes / 1024) : 0)} KB
                                        {a.uploadedByName ? ` · ${a.uploadedByName}` : ""}
                                    </Box>
                                </Box>
                                <IconButton size="small" component="a" href={a.url} target="_blank" rel="noopener noreferrer">
                                    <OpenInNewRoundedIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" sx={{ color: "#dc2626" }}
                                    onClick={() => handleDeleteAttachment(a)}>
                                    <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        ))}

                        <input ref={attachmentInputRef} type="file" hidden
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleAddAttachment(f);
                                e.target.value = "";
                            }} />
                        <ActionButton variant="secondary"
                            startIcon={<UploadFileRoundedIcon sx={{ fontSize: 16 }} />}
                            disabled={!vendorId && !initial?.id}
                            onClick={() => attachmentInputRef.current?.click()}
                            label="파일 추가"
                            sx={{ alignSelf: "flex-start" }} />
                    </Stack>
                </Section>

                <Section title="메모">
                    <TextField multiline minRows={3} fullWidth
                        value={form.memo} onChange={(e) => update("memo", e.target.value)}
                        placeholder="결제 조건, 특이사항, 거래 이력 등" />
                </Section>
        </FormModal>
    );
}

/* ─── 공통 헬퍼 ─────────────────────────────────────── */
function Section({ title, children }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0f172a", mb: 0.8 }}>
                {title}
            </Typography>
            <Divider sx={{ mb: 1.4, borderColor: "#eef2f6" }} />
            {children}
        </Box>
    );
}

function Grid2({ children }) {
    return (
        <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 1.4,
        }}>
            {children}
        </Box>
    );
}

function ImageUploadCard({ title, url, inputRef, onPick, disabled, disabledHint }) {
    return (
        <Box sx={{
            flex: 1, border: "1px dashed #e2e8f0", borderRadius: 1.5, p: 1.5,
            opacity: disabled ? 0.6 : 1,
        }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                    {title}
                </Typography>
                {url && (
                    <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
                        <OpenInNewRoundedIcon fontSize="small" />
                    </IconButton>
                )}
            </Stack>

            {url ? (
                <Box sx={{
                    height: 140, borderRadius: 1, overflow: "hidden",
                    bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Box component="img" src={url} alt={title}
                        sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                </Box>
            ) : (
                <Box sx={{
                    height: 140, borderRadius: 1, bgcolor: "#f8fafc",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#94a3b8", fontSize: 12,
                }}>
                    {disabled ? disabledHint : "이미지가 없습니다"}
                </Box>
            )}

            <input ref={inputRef} type="file" hidden accept="image/*"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onPick?.(f);
                    e.target.value = "";
                }} />
            <ActionButton variant="secondary"
                startIcon={<UploadFileRoundedIcon sx={{ fontSize: 16 }} />}
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
                label={url ? "다시 업로드" : "이미지 선택"}
                sx={{ mt: 1 }} />
        </Box>
    );
}
